import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { mitErsatz } from "@/office/makro/pruefung";
import { readFormenMitUmbruch } from "@/office/word/media";
import { MAKRO_DOKUMENT, VBA_UMBRUCH } from "../makros";

export const lektionTextumbruch: Lesson = {
  id: "textumbruch",
  title: "Textumbruch und Positionierung",
  summary: "Warum Bilder springen – und wie man sie dazu bringt, stehen zu bleiben.",
  durationMinutes: 8,
  learningGoals: [
    "Die Umbrucharten unterscheiden",
    "Den Unterschied zwischen „Mit Text in Zeile“ und frei positioniert verstehen",
    "Bilder verankern",
  ],
  steps: [
    {
      id: "umbrucharten",
      title: "Die Umbrucharten",
      explanation: [
        "Direkt nach dem Einfügen steht ein Bild „Mit Text in Zeile“. Word behandelt es " +
          "dann wie einen sehr großen Buchstaben: Es sitzt in der Textzeile und wandert " +
          "mit dem Text mit. Das ist stabil und für die meisten Fälle die richtige Wahl.",
        "Alle anderen Umbrucharten lösen das Bild aus dem Textfluss: „Quadrat“ und " +
          "„Passend“ lassen den Text außen herumfließen, „Oben und unten“ hält die Zeilen " +
          "darüber und darunter frei, „Hinter den Text“ und „Vor den Text“ legen das Bild " +
          "auf eine eigene Ebene.",
        "Du erreichst die Auswahl über das kleine Symbol rechts neben dem markierten Bild " +
          "oder über „Bildformat“ → „Textumbruch“.",
      ],
      keyPoints: [
        "„Mit Text in Zeile“ ist die stabilste Einstellung.",
        "„Quadrat“ und „Passend“ lassen den Text herumfließen.",
        "„Hinter den Text“ eignet sich für Wasserzeichen, kaum für Inhalte.",
      ],
      task: {
        instruction:
          "Füge ein Bild ein und stelle seinen Textumbruch auf „Quadrat“ oder „Passend“, " +
          "sodass der Fließtext daneben weiterläuft.",
        hint: "Bild anklicken, dann das Symbol „Layoutoptionen“ rechts daneben → „Quadrat“.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const formen = await readFormenMitUmbruch();
              return formen ? formen.map((form) => form.wrap ?? "") : null;
            },
            MAKRO_DOKUMENT,
            (bericht) =>
              [...bericht.gruppe("form").values()].map((form) => VBA_UMBRUCH[Number(form.get("umbruch"))] ?? ""),
            (umbrueche) => {
              const umflossen = umbrueche.filter((art) => ["Square", "Tight", "Through"].includes(art));
              return fromCriteria(
                [criterion(`Ein Bild wird vom Text umflossen (frei positioniert: ${umbrueche.length})`, umflossen.length > 0)],
                {
                  success: "Der Text fließt um das Bild – achte ab jetzt auf den Anker.",
                  failure: "Es gibt noch kein Bild mit dem Umbruch „Quadrat“ oder „Passend“.",
                  hint: "Ein Bild „Mit Text in Zeile“ zählt hier nicht – es ist ja gerade nicht frei positioniert.",
                },
              );
            },
          ),
      },
    },
    {
      id: "anker",
      title: "Der Anker",
      explanation: [
        "Sobald ein Bild nicht mehr in der Zeile steht, bekommt es einen Anker – ein " +
          "kleines Ankersymbol am Rand, das anzeigt, an welchem Absatz das Bild hängt. " +
          "Wird dieser Absatz verschoben oder gelöscht, wandert das Bild mit oder " +
          "verschwindet.",
        "Genau daher kommt das berüchtigte Springen von Bildern: Der Anker sitzt an einem " +
          "anderen Absatz als gedacht. Blende die Anker über „Start“ → Absatzmarken (¶) " +
          "ein, dann siehst du die Zuordnung und kannst den Anker mit der Maus an den " +
          "richtigen Absatz ziehen.",
        "Im Dialog „Weitere Layoutoptionen“ lässt sich zusätzlich „Verankern“ festlegen. " +
          "Damit bleibt das Bild an seinem Absatz, auch wenn der Text darüber wächst.",
        "Der praktische Rat: Wenn ein Bild partout nicht dort bleiben will, wo es soll, " +
          "stelle es auf „Mit Text in Zeile“ und setze es in einen eigenen, zentrierten " +
          "Absatz. Das löst die meisten Probleme sofort.",
      ],
      keyPoints: [
        "Der Anker zeigt, an welchem Absatz das Bild hängt.",
        "Absatzmarken einblenden macht Anker sichtbar.",
        "Im Zweifel: „Mit Text in Zeile“ in einem eigenen, zentrierten Absatz.",
      ],
    },
  ],
};
